import { Alert, DrawerProps, Portal, Typography } from '@mui/material';
import { useCallback } from 'react';
import { useBoolean } from 'usehooks-ts';

import { updateTermsAndConditions } from '@/api/user';

import { FileViewerBottomAction } from '@/components/DataDisplay/File/File.styled';
import { FileFrame } from '@/components/DataDisplay/File/FileFrame';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';
import { useUpdateTermsAndConditions } from '@/hooks/useYesNoQuestion';

import { selectConfigsState } from '@/redux/configs';
import { hideFileViewer, selectUIState } from '@/redux/ui';

import { SourceType } from '@/types/document';

import { getAPIErrorMessage } from '@/utils/api';
import { getDataTestId } from '@/utils/browser';
import { downloadFile } from '@/utils/file';

import { LoadingButton } from '../../Form/LoadingButton';
import { HeaderMobile } from '../../Header/HeaderMobile';
import { Icon } from '../../Icon/Icon';
import { DrawerHeader } from '../../Layout';

import {
  DisclaimerBox,
  ScrollFader,
  StyledDrawer,
  StyledDrawerAction,
  StyledDrawerContent,
} from './TermsAndConditions.styled';

type TermsAndConditionsDrawerProps = Readonly<Omit<DrawerProps, 'onClose'>>;

export function TermsAndConditionsDrawer({
  ...props
}: TermsAndConditionsDrawerProps) {
  const dispatch = useAppDispatch();
  const { value: isBottom, setTrue: setIsBottom } = useBoolean(false);
  const { fileViewer } = useAppSelector(selectUIState);
  const { links } = useAppSelector(selectConfigsState);
  const { t } = useTranslate();

  const { yesButtonProps, error } = useUpdateTermsAndConditions(
    'termsAndConditions',
    updateTermsAndConditions,
  );

  const onScrolledToBottom = useCallback(() => {
    setIsBottom();
  }, [setIsBottom]);

  const onDownloadClick = () => {
    downloadFile(links.appTermsAndConditionsURL, 'terms-and-conditions');
  };

  const onAccept = () => {
    dispatch(hideFileViewer());
    yesButtonProps.onClick();
  };

  return (
    <>
      <StyledDrawer {...props} hideBackdrop={false}>
        <HeaderMobile
          leftSlot={null}
          centerSlot={t('moreInfo.termAndConditionDrawer.header')}
          bgColor={theme => theme.palette.grey[100]}
        />
        <DrawerHeader>
          <Typography variant="h5">
            {t('moreInfo.termAndConditionDrawer.header')}
          </Typography>
        </DrawerHeader>
        <StyledDrawerContent>
          <FileFrame
            src={links.appTermsAndConditionsURL}
            srcType={SourceType.PDF}
            onPDFScrolledToBottom={onScrolledToBottom}
          />
          {!isBottom && <ScrollFader />}
        </StyledDrawerContent>
        {error && (
          <Alert icon={<Icon name="alert" />} color="error" variant="standard">
            {getAPIErrorMessage(error)}
          </Alert>
        )}
        <StyledDrawerAction>
          <LoadingButton
            variant="outlined"
            onClick={onDownloadClick}
            {...getDataTestId(`terms-and-conditions-download-button`)}
          >
            {t('moreInfo.termAndConditionDrawer.button.download')}
          </LoadingButton>
          <LoadingButton
            {...getDataTestId(`terms-and-conditions-continue-button`)}
            disabled={!isBottom}
            isLoading={yesButtonProps.isLoading}
            onClick={yesButtonProps.onClick}
          >
            {t('moreInfo.termAndConditionDrawer.button.acceptAndContinue')}
          </LoadingButton>
        </StyledDrawerAction>
        <DisclaimerBox>
          <Typography variant="caption" textAlign="center">
            {t('moreInfo.termAndConditionDrawer.caption')}
          </Typography>
        </DisclaimerBox>
      </StyledDrawer>
      {fileViewer && (
        <Portal>
          <FileViewerBottomAction>
            <LoadingButton
              variant="outlined"
              onClick={onDownloadClick}
              {...getDataTestId(
                `terms-and-conditions-file-viewer-download-button`,
              )}
            >
              {t('moreInfo.termAndConditionDrawer.button.download')}
            </LoadingButton>
            <LoadingButton
              {...getDataTestId(
                `terms-and-conditions-file-viewer-continue-button`,
              )}
              disabled={!fileViewer.isScrolledToBottom}
              isLoading={yesButtonProps.isLoading}
              onClick={onAccept}
            >
              {t('moreInfo.termAndConditionDrawer.button.acceptAndContinue')}
            </LoadingButton>
          </FileViewerBottomAction>
        </Portal>
      )}
    </>
  );
}
